import { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useStore } from "@/context/StoreContext";
import { getMyOrders, getAddresses, addAddress, deleteAddress, formatPrice, formatApiError } from "@/lib/api";
import { Seo } from "@/components/Seo";
import { Reveal } from "@/components/motion";
import { toast } from "sonner";
import { Heart, LogOut, MapPin, Package, Plus, ShieldCheck, Trash2, User, X } from "lucide-react";

const STATUS_MAP = {
  paid: { label: "Ödendi", cls: "bg-shule-goldlight/70 text-shule-coffee border-shule-gold/40" },
  pending: { label: "Ödeme Bekliyor", cls: "bg-shule-pudra text-shule-coffee border-shule-taupe/40" },
  kapida: { label: "Kapıda Ödeme", cls: "bg-shule-goldlight/70 text-shule-coffee border-shule-gold/40" },
  failed: { label: "Başarısız", cls: "bg-red-50 text-red-800 border-red-200" },
  expired: { label: "Süresi Doldu", cls: "bg-shule-cream text-shule-soft border-shule-line" },
  refunded: { label: "İade Edildi", cls: "bg-shule-cream text-shule-soft border-shule-line" },
};

const FULFILLMENT_LABELS = {
  hazirlaniyor: "Hazırlanıyor", kargoda: "Kargoda", "teslim-edildi": "Teslim Edildi", "iade-edildi": "İade Edildi",
  "havale-bekleniyor": "Havale Bekleniyor", "odeme-bekleniyor": "Ödeme Bekleniyor",
};

function OrdersSection() {
  const [orders, setOrders] = useState(null);

  useEffect(() => {
    getMyOrders().then(setOrders).catch(() => setOrders([]));
  }, []);

  return (
    <Reveal delay={0.18}>
      <div data-testid="orders-section" className="rounded-3xl border border-shule-line bg-shule-paper p-6">
        <div className="flex items-center gap-3 mb-4">
          <Package className="w-5 h-5 text-shule-gold" />
          <h2 className="font-serif text-2xl">Siparişlerim</h2>
        </div>
        {orders === null ? (
          <p className="text-sm text-shule-soft">Yükleniyor…</p>
        ) : orders.length === 0 ? (
          <p data-testid="orders-empty" className="text-sm text-shule-soft leading-relaxed">
            Henüz siparişin yok. İlk siparişin burada ödeme durumuyla birlikte listelenecek.
          </p>
        ) : (
          <ul className="space-y-3">
            {orders.map((o) => {
              const st = STATUS_MAP[o.payment_status] || STATUS_MAP.pending;
              return (
                <li key={o.session_id} data-testid={`order-${o.order_id}`}
                  className="rounded-2xl border border-shule-line bg-shule-base p-4">
                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    <span className="text-xs text-shule-soft font-mono">#{o.order_id}</span>
                    <span data-testid={`order-status-${o.order_id}`} className={`badge-tag border ${st.cls}`}>{st.label}</span>
                  </div>
                  <ul className="mt-2 text-sm text-shule-mocha space-y-0.5">
                    {o.items?.map((it, i) => (
                      <li key={i} className="flex items-center justify-between gap-3">
                        <span>{it.quantity} × {it.name || it.slug}{it.variant ? ` · ${it.variant}` : ""}</span>
                        {it.slug && (
                          <Link to={`/urun/${it.slug}`} className="text-xs font-semibold text-shule-goldhover hover:underline shrink-0">
                            Ürünü Gör
                          </Link>
                        )}
                      </li>
                    ))}
                  </ul>
                  {o.fulfillment_status && (
                    <p data-testid={`order-fulfillment-${o.order_id}`} className="mt-1.5 text-xs font-semibold text-shule-goldhover">
                      Sipariş durumu: {FULFILLMENT_LABELS[o.fulfillment_status] || o.fulfillment_status}
                    </p>
                  )}
                  <div className="mt-2 flex items-center justify-between text-sm">
                    <span className="text-xs text-shule-soft">
                      {new Date(o.created_at).toLocaleDateString("tr-TR", { day: "numeric", month: "long", year: "numeric" })}
                    </span>
                    <span className="font-bold">{formatPrice(o.amount)}</span>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </Reveal>
  );
}

const EMPTY_ADDR = { title: "Ev", full_name: "", phone: "", city: "", address: "", is_default: false };

function AddressesSection() {
  const [addresses, setAddresses] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_ADDR);
  const [saving, setSaving] = useState(false);

  const load = () => getAddresses().then((r) => setAddresses(r.addresses)).catch(() => setAddresses([]));
  useEffect(() => { load(); }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await addAddress(form);
      toast.success("Adres kaydedildi");
      setForm(EMPTY_ADDR);
      setFormOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    try {
      await deleteAddress(id);
      setAddresses((prev) => prev.filter((a) => a.id !== id));
      toast.success("Adres silindi");
    } catch {
      toast.error("Silinemedi");
    }
  };

  const inputCls = "w-full min-h-[48px] rounded-2xl border border-shule-lineaccent bg-shule-base px-4 text-sm outline-none focus:border-shule-gold transition-colors";

  return (
    <Reveal delay={0.14}>
      <div data-testid="addresses-section" className="rounded-3xl border border-shule-line bg-shule-paper p-6">
        <div className="flex items-center justify-between gap-3 mb-4">
          <span className="flex items-center gap-3">
            <MapPin className="w-5 h-5 text-shule-gold" />
            <span>
              <span className="block font-serif text-2xl">Adreslerim</span>
              <span className="block text-xs text-shule-soft">Ödeme adımında hazır bekler</span>
            </span>
          </span>
          <button data-testid="address-add-toggle" onClick={() => setFormOpen((v) => !v)}
            className="min-w-[44px] min-h-[44px] rounded-full border border-shule-lineaccent flex items-center justify-center hover:border-shule-gold transition-colors" aria-label="Adres ekle">
            {formOpen ? <X className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
          </button>
        </div>

        {addresses === null ? (
          <p className="text-sm text-shule-soft">Yükleniyor…</p>
        ) : addresses.length === 0 && !formOpen ? (
          <p data-testid="addresses-empty" className="text-sm text-shule-soft">Kayıtlı adresin yok; ekleyerek ödemeyi hızlandır.</p>
        ) : (
          <ul className="space-y-3">
            {addresses.map((a) => (
              <li key={a.id} data-testid={`address-${a.id}`} className="rounded-2xl border border-shule-line bg-shule-base p-4 flex items-start justify-between gap-3">
                <div>
                  <p className="text-sm font-bold">{a.title} {a.is_default && <span className="badge-tag ml-1">Varsayılan</span>}</p>
                  <p className="text-sm text-shule-mocha mt-1">{a.full_name} · {a.phone}</p>
                  <p className="text-xs text-shule-soft mt-0.5">{a.address}, {a.city}</p>
                </div>
                <button data-testid={`address-delete-${a.id}`} onClick={() => remove(a.id)}
                  className="w-11 h-11 flex items-center justify-center text-shule-soft hover:text-shule-coffee" aria-label="Adresi sil">
                  <Trash2 className="w-4 h-4" />
                </button>
              </li>
            ))}
          </ul>
        )}

        {formOpen && (
          <form data-testid="address-form" onSubmit={save} className="mt-4 space-y-3 rounded-2xl border border-shule-line bg-shule-base p-4">
            <div className="grid grid-cols-2 gap-3">
              <input data-testid="address-title-input" required className={inputCls} value={form.title} onChange={(e) => set("title", e.target.value)} placeholder="Başlık (Ev, İş…)" />
              <input data-testid="address-name-input" required minLength={2} className={inputCls} value={form.full_name} onChange={(e) => set("full_name", e.target.value)} placeholder="Ad Soyad" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <input data-testid="address-phone-input" required minLength={10} className={inputCls} value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="Telefon" />
              <input data-testid="address-city-input" required minLength={2} className={inputCls} value={form.city} onChange={(e) => set("city", e.target.value)} placeholder="Şehir" />
            </div>
            <textarea data-testid="address-text-input" required minLength={5} rows={2} value={form.address} onChange={(e) => set("address", e.target.value)}
              className="w-full rounded-2xl border border-shule-lineaccent bg-shule-paper px-4 py-3 text-sm outline-none focus:border-shule-gold transition-colors resize-none"
              placeholder="Mahalle, sokak, bina ve daire no" />
            <label className="flex items-center gap-2 text-sm text-shule-mocha min-h-[44px] cursor-pointer">
              <input data-testid="address-default-checkbox" type="checkbox" checked={form.is_default} onChange={(e) => set("is_default", e.target.checked)}
                className="w-5 h-5 accent-shule-gold" />
              Varsayılan adresim olsun
            </label>
            <button data-testid="address-save-button" disabled={saving} className="btn-primary-shule w-full disabled:opacity-40">
              {saving ? "Kaydediliyor…" : "Adresi Kaydet"}
            </button>
          </form>
        )}
      </div>
    </Reveal>
  );
}

export default function Account() {
  const { user, logout } = useAuth();
  const { favorites } = useStore();

  if (user === null) {
    return <div data-testid="account-loading" className="container-shule py-24 text-center text-shule-soft font-serif text-2xl">Yükleniyor…</div>;
  }
  if (user === false) {
    return <Navigate to="/giris" replace />;
  }

  return (
    <>
      <Seo title="Hesabım" path="/hesap"  noindex />
      <div className="container-shule py-12 lg:py-20 max-w-2xl">
        <Reveal>
          <p className="eyebrow">Hesabım</p>
          <h1 data-testid="account-greeting" className="mt-2 font-serif text-4xl lg:text-5xl text-shule-coffee">
            Merhaba, <span className="italic text-shule-goldhover">{user.name || "Shule dostu"}</span>
          </h1>
        </Reveal>

        <div className="mt-10 space-y-4">
          <Reveal delay={0.05}>
            <div data-testid="account-profile-card" className="flex items-center gap-4 rounded-3xl border border-shule-line bg-shule-paper p-6">
              <span className="w-14 h-14 rounded-full bg-shule-goldlight border border-shule-gold/40 flex items-center justify-center shrink-0">
                <User className="w-6 h-6 text-shule-goldhover" />
              </span>
              <div className="min-w-0">
                <p className="font-serif text-xl truncate">{user.name}</p>
                <p data-testid="account-email" className="text-sm text-shule-soft truncate">{user.email}</p>
                {user.role === "admin" && <span className="badge-tag mt-1 inline-block">Yönetici</span>}
              </div>
            </div>
          </Reveal>

          {user.role === "admin" && (
            <Reveal delay={0.08}>
              <Link to="/admin" data-testid="account-admin-link"
                className="flex items-center justify-between rounded-3xl border border-shule-gold/40 bg-shule-goldlight/30 p-6 hover:border-shule-gold transition-colors min-h-[72px]">
                <span className="flex items-center gap-3">
                  <ShieldCheck className="w-5 h-5 text-shule-goldhover" />
                  <span>
                    <span className="block font-semibold text-sm">Yönetim Paneli</span>
                    <span className="block text-xs text-shule-soft">Siparişler, ürünler, başvurular</span>
                  </span>
                </span>
                <span className="badge-tag">Aç</span>
              </Link>
            </Reveal>
          )}

          <OrdersSection />
          <AddressesSection />

          <Reveal delay={0.1}>
            <Link to="/favoriler" data-testid="account-favorites-link"
              className="flex items-center justify-between rounded-3xl border border-shule-line bg-shule-paper p-6 hover:border-shule-gold transition-colors min-h-[72px]">
              <span className="flex items-center gap-3">
                <Heart className="w-5 h-5 text-shule-gold" />
                <span>
                  <span className="block font-semibold text-sm">Favorilerim</span>
                  <span className="block text-xs text-shule-soft">Tüm cihazlarında senkronize</span>
                </span>
              </span>
              <span data-testid="account-favorites-count" className="font-serif text-2xl text-shule-goldhover">{favorites.length}</span>
            </Link>
          </Reveal>

          <Reveal delay={0.2}>
            <button data-testid="account-logout-button" onClick={logout}
              className="w-full flex items-center justify-center gap-2 rounded-3xl border border-shule-lineaccent p-5 text-sm font-semibold text-shule-mocha hover:border-shule-gold hover:text-shule-goldhover transition-colors min-h-[56px]">
              <LogOut className="w-4 h-4" /> Çıkış Yap
            </button>
          </Reveal>
        </div>
      </div>
    </>
  );
}
